import { serverUrl } from '../config';
import { z } from 'zod';

const letterTypeSchema = z.union([z.literal('standard'), z.literal('invitational')]);
export type LetterType = z.infer<typeof letterTypeSchema>;

const nullishToUndefined = <T>(x: T) => (x === null ? undefined : x);

export const draftDataSchema = z.object({
  id: z.string(),
  title: z.string(),
  body: z.string(),
  updated_at: z.string(),
  temp_contact_email: z.string().nullish().transform(nullishToUndefined),
  temp_contact_name: z.string().nullish().transform(nullishToUndefined),
  first_name: z.string().nullish().transform(nullishToUndefined),
  last_name: z.string().nullish().transform(nullishToUndefined),
  letter_type: letterTypeSchema,
});

export type DraftData = z.infer<typeof draftDataSchema>;

export const fetchDraftForRecipient =
  (token: string) => (recipient: string) => (): Promise<DraftData> =>
    fetch(`${serverUrl}/api/letters/find-or-create/draft`, {
      method: 'put',
      headers: {
        authorization: `Token ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ recipient }),
    }).then((res) => res.json());

export const fetchDraft =
  (token: string) =>
  (letterId: string): Promise<DraftData> =>
    fetch(`${serverUrl}/api/letters/draft/${letterId}/`, {
      method: 'get',
      headers: {
        authorization: `Token ${token}`,
        'Content-Type': 'application/json',
      },
    })
      .then((res) => {
        if (!res.ok) {
          throw Error(res.statusText);
        } else {
          return res;
        }
      })
      .then((res) => res.json());

export type SyncError = 'Size Limit';
export type SchemaError<T> = { in: string[]; message: T; schema: string };

export type SyncResponse =
  | { status: 'ok' }
  | { status: 'error'; errors: SchemaError<SyncError>[] }
  | { status: 'failed'; reason: any };

export const syncDraft =
  (token: string) =>
  (id: string, title: string, body: string): Promise<SyncResponse> =>
    fetch(`${serverUrl}/api/letters/draft/${id}/`, {
      method: 'post',
      headers: {
        'Content-Type': 'application/json',
        authorization: `Token ${token}`,
      },
      body: JSON.stringify({ id, title, body }),
    })
      .then((res) => res.json())
      .then((r: any) => (r.errors ? { status: 'error', errors: r.errors } : r))
      .catch((reason) => ({ status: 'failed', reason: reason }));

export interface CheckOutdatedResponse {
  outdated: boolean;
}

const createSHA1 = async (title: string | undefined, body: string | undefined): Promise<string> => {
  const encoder = new TextEncoder();
  const edible = encoder.encode(`${title}|${body}`);
  const digest = await crypto.subtle.digest('SHA-1', edible);

  return Array.from(new Uint8Array(digest))
    .map((byte: number) => byte.toString(16).padStart(2, '0'))
    .join('');
};

export const checkOutdated =
  (token: string) =>
  async (
    id: string,
    title: string | undefined,
    body: string | undefined
  ): Promise<CheckOutdatedResponse> => {
    const hash = await createSHA1(title, body);
    const messageBody = { id, hash };

    return await fetch(`${serverUrl}/api/letters/draft/${id}/check-outdated`, {
      method: 'post',
      headers: {
        'Content-Type': 'application/json',
        authorization: `Token ${token}`,
      },
      body: JSON.stringify(messageBody),
    })
      .then((res) => res.json())
      .catch((e) => {
        console.error(e);
        return { outdated: false };
      }); // TODO: Handle this
  };

interface DeleteLetter {
  status: 'ok' | 'error';
}

export const deleteLetter = (token: string, id: string): Promise<DeleteLetter> =>
  fetch(`${serverUrl}/api/letters/draft/${id}/`, {
    method: 'delete',
    headers: { authorization: `Token ${token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify({ id: id }),
  }).then((res) => res.json());

export const deleteLetterNew = (token: string) => (id: string) => deleteLetter(token, id);

interface SendLetter {
  status: 'ok' | 'error';
  postSendAction: 'none' | 'redirectToInvitations' | undefined;
  errorId?: 'rate-limit';
}

export const sendLetter =
  (token: string) =>
  (id: string): Promise<SendLetter> => {
    const fetchUrl = `${serverUrl}/api/letters/draft/${id}/send`;
    const request = {
      method: 'post',
      headers: { authorization: `Token ${token}` },
    };
    return fetch(fetchUrl, request)
      .then((res) => res.json())
      .then((x) => {
        console.log(x);
        return x;
      });
  };

export const updateTemporaryContact =
  (token: string) =>
  (id: string, { name, email }: { name?: string; email?: string }) => {
    const fetchUrl = `${serverUrl}/api/letters/draft/${id}/temporary-contact`;
    const request = {
      method: 'post',
      headers: { authorization: `Token ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, name }),
    };
    return fetch(fetchUrl, request).then((res) => res.json());
  };
